import { FC, useState } from 'react'
import {
  DatePickerField,
  GreyGrey60,
  Primary12Regular,
  TextInputField,
  SimpleModal,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useForm } from '@sequencehq/utils'
import { required, isPositiveInteger } from '@sequencehq/utils/validation'
import { useNotifications } from '@sequencehq/core-components'
import { useMutation } from '@sequencehq/api/utils'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { format, parseISO } from '@sequencehq/utils/dates'
import { SeatTypeSelect } from 'Seats/SeatMetric/SeatTypeSelect'

type FormValues = {
  customerAlias: string
  seatType: string
  eventTimestamp: string
  total: string
}

type Props = {
  isOpen: boolean
  onClose: () => void
  seatEvent: DashboardApi20240730.Seats.SeatEvent
}

export const OverwriteSeatEventDrawer: FC<Props> = ({
  isOpen,
  onClose,
  seatEvent
}) => {
  const { displayNotification } = useNotifications()
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const form = useForm<FormValues>({
    value: {
      customerAlias: seatEvent.customerAlias,
      seatType: seatEvent.seatType,
      eventTimestamp: format(parseISO(seatEvent.eventTimestamp), 'yyyy-MM-dd'),
      total: String(seatEvent.total)
    },
    showValidationErrors,
    fieldConfiguration: [
      { property: 'customerAlias', validation: [required] },
      { property: 'seatType', validation: [required] },
      { property: 'eventTimestamp', validation: [required] },
      { property: 'total', validation: [isPositiveInteger] }
    ]
  })

  const overwriteSeatEventMutation = useMutation(
    dashboard20240730Client.postSeatEvent
  )

  const handleSubmit = async () => {
    setShowValidationErrors(true)

    if (!form.queries.isValid) {
      return false
    }

    try {
      await overwriteSeatEventMutation.mutateAsync({
        customerAlias: form.queries.formData.customerAlias,
        customerEventId: seatEvent.customerEventId,
        seatType: form.queries.formData.seatType,
        eventTimestamp: `${form.queries.formData.eventTimestamp}T00:00:00.000Z`,
        total: Number(form.queries.formData.total)
      })
      displayNotification('Seat event updated successfully', {
        type: 'success'
      })
      onClose()
      return true
    } catch {
      displayNotification('Failed to update seat event', {
        type: 'error'
      })
      return false
    }
  }

  const handleClose = () => {
    form.mutators.resetFormData()
    onClose()
  }

  return (
    <SimpleModal
      data-testid="overwriteSeatEvent.drawer"
      isOpen={isOpen}
      variant="drawer"
      title="Overwrite seat event"
      cancelButtonText="Cancel"
      submitButtonText="Overwrite event"
      submittingButtonText="Updating..."
      onSubmit={handleSubmit}
      onClose={handleClose}
    >
      <Flex direction="column" gap="16px" data-testid="overwriteSeatEvent.form">
        <Box {...Primary12Regular} color={GreyGrey60}>
          Creates a new event with the same ID, overwriting the previous one.
        </Box>

        <TextInputField
          data-testid="overwriteSeatEvent.customerAlias"
          label="Customer alias"
          value={form.fields.customerAlias.value}
          onChange={e => form.fields.customerAlias.onChange(e.target.value)}
          validationErrors={form.fields.customerAlias.validationErrors}
        />

        <TextInputField
          data-testid="overwriteSeatEvent.customerEventId"
          label="Customer event ID"
          value={seatEvent.customerEventId}
          disabled
        />

        <SeatTypeSelect
          data-testid="overwriteSeatEvent.seatType"
          label="Seat type"
          value={form.fields.seatType.value}
          onChange={form.fields.seatType.onChange}
          allowCreate={false}
        />

        <DatePickerField
          data-testid="overwriteSeatEvent.eventTimestamp"
          width="100%"
          label="Event timestamp"
          placeholder="Select date"
          value={
            form.fields.eventTimestamp.value
              ? new Date(form.fields.eventTimestamp.value)
              : undefined
          }
          onChange={value =>
            form.fields.eventTimestamp.onChange(
              value ? format(value, 'yyyy-MM-dd') : ''
            )
          }
          validationErrors={form.fields.eventTimestamp.validationErrors}
        />

        <TextInputField
          data-testid="overwriteSeatEvent.total"
          label="Total"
          type="number"
          value={form.fields.total.value}
          onChange={e => form.fields.total.onChange(e.target.value)}
          validationErrors={form.fields.total.validationErrors}
        />
      </Flex>
    </SimpleModal>
  )
}
